import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { SegmentAttributeGroup, SegmentCompositionSelection, SegmentResults } from '../models/segments';
import { AppContext, AppProviderDispatch } from '../shared/app-context';
import useRestService from '../shared/rest-service';
import { unmapTransactionalAttributes } from '../utilities/attribute-filter';

export const useSegmentResults = (
  includedAttributes: SegmentAttributeGroup,
  excludedAttributes?: SegmentAttributeGroup,
  includedComposition?: SegmentCompositionSelection,
  excludedComposition?: SegmentCompositionSelection
) => {
  const appState = React.useContext<AppProviderDispatch>(AppContext);
  const { fetchWrapper } = useRestService();

  const hasIncludeAttributes = !!includedAttributes && includedAttributes.predicates.length > 0;
  const hasExcludeAttributes = !!excludedAttributes && excludedAttributes.predicates.length > 0;
  const hasIncludedComposition = !!includedComposition?.segmentId;
  const hasExcludedComposition = !!excludedComposition?.segmentId;
  const enabled = hasIncludeAttributes || hasExcludeAttributes;

  const attributesValid = React.useMemo(() => {
    return !!includedAttributes && !!excludedAttributes;
  }, [includedAttributes, excludedAttributes]);

  const queryKey = [
    'segment-results',
    appState.state.clientId,
    includedAttributes,
    excludedAttributes,
    includedComposition,
    excludedComposition
  ];

  const cleanUpNewSegment = (key, value) => {
    if (key === 'composition' && Object.keys(value).length === 0) {
      return undefined;
    }
    return value;
  };

  return useQuery<SegmentResults, Error>(
    queryKey,
    () => {
      let selectedComposition = {};

      if (hasIncludedComposition) {
        selectedComposition['include'] = {
          ...includedComposition
        };
      }
      if (hasExcludedComposition) {
        selectedComposition['exclude'] = {
          ...excludedComposition
        };
      }

      return fetchWrapper(`api/${appState.state.clientId}/segments/customers`, {
        method: 'POST',
        body: JSON.stringify(
          {
            attributes: {
              include: {
                ...includedAttributes,
                predicates: unmapTransactionalAttributes(includedAttributes?.predicates)
              },
              exclude: {
                ...excludedAttributes,
                predicates: unmapTransactionalAttributes(excludedAttributes?.predicates)
              }
            },
            composition: {
              ...selectedComposition
            }
          },
          cleanUpNewSegment
        )
      });
    },
    {
      enabled: enabled && attributesValid,
      keepPreviousData: enabled
    }
  );
};
